import {
  Box,
  List,
  ListItem,
  ListItemText,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { ReactNode, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLoaderData, useNavigate, useNavigation, useOutletContext } from "react-router";
import {
  EntityTypes,
  InviteStatus,
  Permit,
  PermitStatus,
  RevisionQueueMessage,
} from "types";
import { formatLabel, formatPaymentMethod, formatCurrency } from "utils";
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from "material-react-table";
import dayjs from "dayjs";
import { PermitFormContext } from "./PermitFormLayout";
import { PermitPanelRouteParams } from "./PermitFormLayout";
import DetailsHeader from "components/layout/DetailsHeader";
import { getRevisions } from "api";
import FormFrame from "components/layout/FormFrame";
import {
  TableType,
  useDefaultTableOptions,
} from "hooks/useDefaultTableOptions";
export interface PermitHistoryRow {
  id: string;
  updated: Date;
  event: string;
  transactionID: string;
  amount: string;
  paymentMethod: string;
  email: string;
}

export const permitHistoryLoader = async ({
  params,
}: {
  params: PermitPanelRouteParams["params"];
}): Promise<RevisionQueueMessage<Permit>[]> => {
  const { permitID } = params;
  const _revisions = await getRevisions(EntityTypes.Permit, permitID || "");
  return _revisions.filter(
    (revision) => revision.entityType === EntityTypes.Permit,
  ) as RevisionQueueMessage<Permit>[];
};

const PermitHistory = (): ReactNode => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { breakpoints } = theme;
  const mobile = useMediaQuery(breakpoints.down("lg"));
  const navigate = useNavigate();
  const navigation = useNavigation();
  const { permit } = useOutletContext<PermitFormContext>();
  const data = useLoaderData() as Promise<RevisionQueueMessage<Permit>[]>;
  const [revisions, setRevisions] = useState<RevisionQueueMessage<Permit>[]>(
    [],
  );

  useEffect(() => {
    const fetchData = async () => {
      const _revisions = await data;
      setRevisions(_revisions);
    };
    fetchData();
  }, [data]);

  const loading = useMemo(() => {
    if (!revisions) return true;
    if (navigation.state === "loading") return true;
    return false;
  }, [revisions, navigation.state]);

  const permitHistory: PermitHistoryRow[] = useMemo(() => {
    if (!permit || !revisions) return [];
    const data: PermitHistoryRow[] = [];

    revisions.forEach((revision, index) => {
      const { action, details } = revision.actionData;
      
      // skip sent notifications revisions
      if (action &&
        action.toLowerCase().trim() === "sent_notification"
      ) {
        return;
      }

      // special case for guest permit created
      if (details && details.toLowerCase().trim() === "guest permit created") {
        data.push({
          id: `revision-${index}`,
          updated: new Date(revision.timestamp * 1000),
          event: `guest permit ${revision.data.permitNumber} created`,
          transactionID: "",
          amount: "",
          paymentMethod: "",
          email: revision.data?.contact?.primaryEmail || "",
        })
      }

      // general permit mutations
      let event: string = revision.data.permitStatus;
      if (
        event === PermitStatus.PendingRegistration &&
        revision.data.inviteStatus === InviteStatus.Sent
      ) {
        event = `invite ${revision.data.inviteStatus}`;
      }

      if (data.length > 0 && data[data.length - 1].event === event) {
        return;
      }

      data.push({
        id: `revision-${index}`,
        updated: new Date(revision.timestamp * 1000),
        event: event,
        transactionID: "",
        amount: "",
        paymentMethod: "",
        email: revision.data?.contact?.primaryEmail || "",
      });
    });

    // add transaction history
    const transactions = permit.transactions.map(
      (transaction, index) => ({
        id: `transaction-${index}`,
        updated: new Date(transaction?.updated || ""),
        event: transaction.status,
        transactionID: transaction.paymentID,
        amount: transaction.data?.details?.amount
          ? formatCurrency(transaction.data.details.amount)
          : "-",
        paymentMethod: formatPaymentMethod(
          transaction.data?.paymentMethodDetails,
        ),
        email: permit.contact.primaryEmail || "-",
      }),
    );
    data.push(...transactions);

    data.sort((a, b) => b.updated.getTime() - a.updated.getTime());
    return data;
  }, [permit, revisions]);

  const columns = useMemo<MRT_ColumnDef<PermitHistoryRow>[]>(
    () => [
      {
        header: t("history_table.date", { ns: "permits" }),
        id: "date",
        accessorFn: (row) => {
          return dayjs(row.updated).format("MM/DD/YYYY");
        },
        size: 40,
      },
      {
        header: t("history_table.time", { ns: "permits" }),
        id: "time",
        accessorFn: (row) => {
          return dayjs(row.updated).format("h:mm A");
        },
        size: 40,
      },
      {
        header: t("status"),
        accessorKey: "event",
        accessorFn: (row) => {
          return formatLabel(row.event);
        },
        size: 50,
      },
      {
        header: t("history_table.transaction_id", { ns: "permits" }),
        accessorKey: "transactionID",
      },
      {
        header: t("history_table.amount", { ns: "permits" }),
        accessorKey: "amount",
        Cell: (cell) => {
          return (
            <Typography sx={{ pr: 2 }}>{cell.renderedCellValue}</Typography>
          );
        },
        size: 50,
      },
      {
        header: t("history_table.payment_method", { ns: "permits" }),
        accessorKey: "paymentMethod",
      },
      {
        header: t("history_table.email", { ns: "permits" }),
        accessorKey: "email",
      },
    ],
    [],
  );

  const tableDefaults = useDefaultTableOptions<PermitHistoryRow>(
    TableType.NESTED,
  );
  const table = useMaterialReactTable({
    ...tableDefaults,
    data: permitHistory,
    columns,
    enableRowActions: false,
    enableTopToolbar: false,
    enableTableFooter: false,
    enableBottomToolbar: false,
    enableColumnActions: false,
    enablePagination: false,
    enableStickyHeader: true,
    enableDensityToggle: false,
    enableRowVirtualization: false,
    enableFullScreenToggle: false,
    enableSorting: true,
    enableColumnFilterModes: false,
    enableGlobalFilter: false,
    enableTableHead: true,
    enableRowSelection: false,
    enableExpandAll: false,
    enableColumnOrdering: false,
    enableStickyFooter: false,
    enableColumnFilters: false,
    initialState: {
      showColumnFilters: false,
      showGlobalFilter: false,
      density: "comfortable",
    },
    state: {
      isLoading: loading,
      showLoadingOverlay: false,
    },
    getRowId: (row) => row.id,
  });

  if (mobile) {
    const itemHeight = 70;
    const renderItem = (row: PermitHistoryRow, index: number): ReactNode => {
      return (
        <ListItem
          key={`permit-${row.id}-${index}`}
          id={index.toString()}
          divider
          sx={{
            height: itemHeight,
          }}
        >
          <ListItemText
            primary={`${formatLabel(row.event)} ${row.amount} ${
              row.paymentMethod
            }`}
            secondary={`${dayjs(row.updated).format("MM/DD/YY h:mm A")}`}
            primaryTypographyProps={{
              variant: "body2",
              noWrap: true,
            }}
            secondaryTypographyProps={{
              variant: "body2",
              noWrap: true,
            }}
          />
        </ListItem>
      );
    };
    return (
      <FormFrame loading={loading}>
        <DetailsHeader
          title={t("subheadings.history", { ns: "permits" })}
          hideSubmit
          onCancel={() => navigate(-1)}
        />
        <Box
          sx={{
            maxHeight: `calc(100dvh - 388px)`,
            overflowY: "auto",
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            pr: 1,
          }}
        >
          <List>
            {permitHistory.map((row, index) => renderItem(row, index))}
          </List>
        </Box>
      </FormFrame>
    );
  }

  return (
    <React.Fragment>
      <FormFrame loading={loading}>
        <DetailsHeader
          title={t("subheadings.history", { ns: "permits" })}
          hideSubmit
          onCancel={() => navigate(-1)}
        />
        <MaterialReactTable table={table} />
      </FormFrame>
    </React.Fragment>
  );
};

export default PermitHistory;
